// The demo console's API: a few paths to the real api, everything else answered in the browser (spec/24 §1, §4.2).
//
// The generated client is handed this `fetch` instead of the browser's. It sends exactly these requests to
// the network — better-auth's `/v1/auth/*` (sign-in, sign-up, the session), `/v1/me/demo` (the window),
// `/v1/meta` (the brand and the legal versions sign-up shows), and `GET /v1/me` with `POST /v1/me/profile/complete`
// (a new Google, Discord or Steam account finishing its profile, saved on the real account) — and answers every other one here: first from the
// simulated venue (`venue-api.ts`, the live screens), then from the console's own fixtures
// (`src/testing/fake-server.ts`, every other screen). Nothing about a venue, a station or a session ever leaves the
// page, and the demo origin's edge forwards only the same paths (`pages/proxy.ts`), so it could not if it
// tried.
//
// Before the visitor's demo has started there is no venue to answer from (`runtime.store` is `null`); a request
// for one is answered `503`, which no screen reaches, because the frame shows none until the window has started
// (`DemoFrame.tsx`).

import type { FetchLike } from '@clavian/api-client';
import { ORGANIZATION_ROLE_GRANTS } from '@clavian/contracts';
import { DEMO_ORGANIZATION, DEMO_VENUE, type DemoStore } from '@clavian/demo';

import { createFakeServer, type FakeServer } from '../testing/fake-server';
import { answerFromDemoVenue, DEMO_REQUEST_ID } from './venue-api';

/**
 * What the demo sends to the real api, and nothing else (spec/24 §1, §5.2): sign-in, the window, the brand, and a new
 * social account's profile — `GET /v1/me` and `POST /v1/me/profile/complete`. Mirrors the edge (`pages/proxy.ts`).
 */
export function isRealApiPath(pathname: string, method = 'GET'): boolean {
  const verb = method.toUpperCase();
  if (pathname.startsWith('/v1/auth/') || pathname === '/v1/me/demo' || pathname === '/v1/meta') return true;
  if (pathname === '/v1/me') return verb === 'GET' || verb === 'HEAD';
  return pathname === '/v1/me/profile/complete' && verb === 'POST';
}

/** What the backend reads the simulated venue through: set by the frame once the window has started. */
export interface DemoRuntime {
  store: DemoStore | null;
}

export function createDemoRuntime(): DemoRuntime {
  return { store: null };
}

/** The frame hands the venue to the backend once the window has started… */
export function attachDemoStore(runtime: DemoRuntime, store: DemoStore): void {
  runtime.store = store;
}

/** …and takes it back when the window ends or the frame goes, unless a newer one has replaced it. */
export function detachDemoStore(runtime: DemoRuntime, store: DemoStore): void {
  if (runtime.store === store) runtime.store = null;
}

/** The console's fixtures as the demo uses them: signed in as the demo organisation's owner, at a venue in full swing. */
export function createDemoFixtures(): FakeServer {
  return createFakeServer({
    signedIn: true,
    liveVenue: true,
    catalogueShowcase: true,
    stationsOnline: true,
    organizationRole: 'owner',
    permissions: [...ORGANIZATION_ROLE_GRANTS.owner],
    venue: { name: DEMO_VENUE.name, slug: DEMO_VENUE.slug },
    organizationName: DEMO_ORGANIZATION.name,
  });
}

export interface DemoFetchOptions {
  readonly runtime: DemoRuntime;
  /** The browser's own `fetch`, for the real paths. */
  readonly network: FetchLike;
  /** The console's fixtures, made on first use. */
  readonly fixtures?: () => FakeServer;
  readonly now?: () => number;
}

const json = (status: number, body: unknown): Response =>
  new Response(status === 204 ? null : JSON.stringify(body), {
    status,
    headers: { 'content-type': status >= 400 ? 'application/problem+json' : 'application/json' },
  });

/** The fixtures' request log grows with every poll; the demo trims it so a 15-minute visit stays small. */
const REQUEST_LOG_LIMIT = 200;

export function createDemoFetch(options: DemoFetchOptions): FetchLike {
  const now = options.now ?? (() => Date.now());
  let fixtures: FakeServer | undefined;
  const fixturesNow = (): FakeServer => (fixtures ??= (options.fixtures ?? createDemoFixtures)());

  return async (input, init) => {
    const url = new URL(input, 'http://demo.invalid');
    if (isRealApiPath(url.pathname, init.method)) return options.network(input, init);

    const store = options.runtime.store;
    if (store === null) {
      return json(503, { type: 'https://errors.clavian.io/internal-error', title: 'The demo has not started', status: 503, code: 'internal_error', requestId: DEMO_REQUEST_ID });
    }

    const body = init.body === undefined || init.body === null || typeof init.body !== 'string' || init.body === '' ? {} : (JSON.parse(init.body) as Record<string, unknown>);
    const answered = answerFromDemoVenue(store, init.method.toUpperCase(), url.pathname, url.searchParams, body, now());
    if (answered !== undefined) return json(answered.status, answered.body);

    const server = fixturesNow();
    if (server.state.requests.length > REQUEST_LOG_LIMIT) server.state.requests.splice(0, server.state.requests.length - REQUEST_LOG_LIMIT);
    return server.fetch(`${url.pathname}${url.search}`, { method: init.method, headers: new Headers(init.headers), ...(typeof init.body === 'string' ? { body: init.body } : {}) });
  };
}
